/*
 __________________________________
/ This is a good place for general \
\ purpose validators               /
 ----------------------------------
        \   ^__^
         \  (oo)\_______
            (__)\       )\/\
                ||----w |
                ||     ||

In this context:

    VALIDATOR (noun) -  A function that takes returns a boolean describing the validity of a given value.
                        Validators' names should be predicates.

    GENERATOR (noun) -  A function that takes parameters then MAKEs and returns a VALIDATOR.
                        Generators' names should start with `make` for consistency.

Most validators should treat empty or missing values as VALID.

*/
import isString from 'lodash/isString';
import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';

/**
 * Validator: Tests that the value is present and not empty. Useful for `required`.
 *
 * @param value
 * @returns {boolean}
 */
export const isProvided = (value) => {
    let result;

    if (isString(value)) {
        result = Boolean(value.trim());
    } else if (isBoolean(value)) {
        result = value;
    } else {
        result = !isEmpty(value);
    }

    return result;
};

/**
 * Validator: Ensures that the inputted string is not entirely spaces.
 *
 * @param value {string}
 * @returns {boolean}
 */
export const hasContent = (value) => !value || value.trim() !== '';

/**
 * Generator: Makes an isOneOf validator.
 * Validator: Tests that the value is one of the elements in `list`
 *
 * @param list {array}
 * @returns {function}
 */
export const makeIsOneOf = (list = []) => (value) => list.includes(value);

/**
 * Generator: Makes a matchesRegex validator.
 * Validator: Tests that the value satisfies the `regexPattern`s `test` method.
 *
 * @param regexPattern
 * @returns {function}
 */
export const makeMatchesRegex = (regexPattern) => (value) =>
    !value || regexPattern.test(`${value}`);

/**
 * Validator: Tests that the value is an integer
 *
 * @param value
 * @returns {boolean}
 */
export const isAnInteger = makeMatchesRegex(
    /^(-{1})?[0-9]+(\.0*)?(?:e[+-][0-9]+)?$/,
);

/**
 * Validator: Tests that the value is an integer
 *
 * @param value
 * @returns {boolean}
 */
export const isANumber = makeMatchesRegex(
    /^(?:[0-9]+|[0-9]*\.[0-9]+)(?:e[+-][0-9]+)?$/,
);

/**
 * Validator: Tests that the value is a whole number
 *
 * @param value
 * @returns {boolean}
 */
export const isAWholeNumber = (value) =>
    !value || (isAnInteger(value) && parseInt(value, 10) >= 0);

/**
 * Generator: Makes a hasMaxLength validator.
 * Validator: Tests that the value's length is equal to or shorter than `maxLength`.
 *
 * @param maxLength
 * @returns {function}
 */
export const makeHasMaxLength = (maxLength) => (value) =>
    !value || value.length <= maxLength;

/**
 * Generator: Makes a hasMinLength validator.
 * Validator: Tests that the value's length is equal to or longer than `minLength`.
 *
 * @param minLength
 * @returns {function}
 */
export const makeHasMinLength = (minLength) => (value) =>
    !value || value.length >= minLength;

/**
 * Generator: Makes an isInRange validator.
 * Validator: Tests that the value is within a range (inclusive)
 *
 * @param low - bottom end of range
 * @param high - top end of range
 * @returns {function}
 */
export const makeIntIsInRange = (low, high) => (value) => {
    if (!value) {
        return true;
    }

    const intValue = parseInt(value, 10);
    const intLow = parseInt(low, 10);
    const intHigh = parseInt(high, 10);

    return intValue >= intLow && intValue <= intHigh;
};
